import { type GeoTripData } from 'types/geo.types';
import dayjs from 'dayjs';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import type DriverBehaviorHandler from 'handlers/driver.behavior.handler';
import saveAs from 'file-saver';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';

interface TimeRange {
  start: string;
  end: string;
}

interface Event {
  type: string;
  timestamp: string;
}

export const mergeGeoPoints = (
  gpsData: GeoTripData[],
  startDateTime: string,
  endDateTime: string
): GeoTripData[] => {
  if (!gpsData) return [];
  return gpsData.filter((point) => {
    const pointTime = dayjs(point.sample_timestamp);
    return (
      pointTime.isAfter(dayjs(startDateTime)) &&
      pointTime.isBefore(dayjs(endDateTime))
    );
  });
};

export const filterDataPointsByTimeRange = (
  selectedTimeRange: TimeRange | null,
  data: GeoTripData[]
): GeoTripData[] => {
  if (!selectedTimeRange || !data) return data;
  return data.filter((point) => {
    const pointTime = dayjs(point.sample_timestamp);
    return (
      pointTime.isAfter(dayjs(selectedTimeRange.start)) &&
      pointTime.isBefore(dayjs(selectedTimeRange.end))
    );
  });
};

export const findEventsAtDate = (
  timeLineData: Event[],
  date: string
): Event[] => {
  if (!timeLineData || !date) return [];
  return timeLineData.filter((event) => {
    const eventTime = dayjs(event.timestamp);
    return eventTime.isSame(dayjs(date), 'minute');
  });
};

export const formatEventName = (event: Event): string => {
  if (!event) return '';
  return `${event.type} - ${dayjs(event.timestamp).format('HH:mm:ss')}`;
};

export enum EVENT_COLORS {
  PRODUCTIVE = '#EBCB8B', // muted amber
  SHORT_IDLING = '#EAB308',
  EXCESS_IDLING = '#EF4444',
  ENGINE_HOURS = '#7BAAF7', // calm sky blue
  LOADING_SPOTTING = '#818CF8', // indigo-violet (distinct from GPS loading teal)
  UNLOADING_SPOTTING = '#D3B5E5', // pastel lilac
  MUCKING = '#7FCB8A', // loading - soft green
  MOVING_LOADED = '#4FAF6A', // moving loaded - deeper green
  MOVING_UNLOADED = '#8EC5E8', // moving empty - soft blue
  DUMPING_STOP = '#E07A7A', // soft red
  IDLING_STOP = '#D6CFCB', // warm gray (lighter, distinct from teal)
  QUEUING = '#F59E0B',
  PARKING = '#64748B',
  ALLOWED_BREAK = '#C9A2D8', // pastel purple
  WARMUP = '#7FD3C2',
  COOLDOWN = '#95A5A6',
  SHUTDOWN = '#5D6D7E',
}

const DEFAULT_EVENT_COLOR = EVENT_COLORS.ENGINE_HOURS;

// Returns a plain CSS color string (previously wrapped in Cesium's
// `Color.fromCssColorString`) — MapLibre paint properties take CSS color
// strings directly, so the Cesium `Color` parse/validate step is no longer
// needed.
export const getEventColor = (event?: string | null): string => {
  const raw = typeof event === 'string' ? event.trim() : '';
  if (!raw) return DEFAULT_EVENT_COLOR;

  // These can show up as `customEventType` (mixed case) even though the timeline
  // uses different keys.
  if (raw === 'Loading') return '#14B8A6';
  if (raw === 'Unloading') return '#E07A7A';

  const normalized = raw.replace(/\s+/g, '_').replace(/-+/g, '_');
  const key = normalized.toUpperCase() as keyof typeof EVENT_COLORS;
  return EVENT_COLORS[key] ?? DEFAULT_EVENT_COLOR;
};
export const downloadEngineHoursTimeline = async (
  returnType: 'excel' | 'pdf' = 'excel',
  startDateWithTime: string,
  endDateWithTime: string,
  customerId: string,
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
  timezone: string,
  driverBehaviorHandler: DriverBehaviorHandler,
  setIsExporting: (isExporting: boolean) => void,
  setdisableDownload: (disableDownload: boolean) => void,
  isSiteSummary: boolean = false
): Promise<void> => {
  if (!startDateWithTime || !endDateWithTime) return;
  setIsExporting(true);
  setdisableDownload(true);
  try {
    const bytes = await driverBehaviorHandler.downloadEngineHoursTimeline(
      customerId,
      startDateWithTime,
      endDateWithTime,
      preferredTimeDisplayMode,
      timezone,
      returnType,
      isSiteSummary
    );

    const fileExtension = returnType === 'excel' ? 'xlsx' : 'pdf';

    const blob = new Blob([bytes], {
      type:
        returnType === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
    });
    // Filenames must carry the customer/display timezone, not raw UTC. Colons are
    // stripped by the OS on download, so format with `_` separators up front.
    const fileNameTimestamp = (utcTime: string) =>
      convertUtcToDisplayTime(
        utcTime,
        preferredTimeDisplayMode,
        timezone,
        'YYYY-MM-DD HH_mm_ss'
      );

    saveAs(
      blob,
      `${
        isSiteSummary ? 'Site Summary Report' : 'Engine-Hours-Timeline'
      } - ${fileNameTimestamp(startDateWithTime)}-${fileNameTimestamp(
        endDateWithTime
      )}.${fileExtension}`
    );
  } finally {
    setIsExporting(false);
    setdisableDownload(false);
  }
};
